import { useState, useEffect } from 'react';
import { getRecycleBin, restoreJob, purgeJob, getAuditLog } from '../utils/api';
import toast from 'react-hot-toast';

const GREEN = '#0D3B2E';
const money = n => '$' + (parseFloat(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
const when = s => s ? new Date(s.includes('Z') || s.includes('T') ? s : s.replace(' ', 'T') + 'Z').toLocaleString() : '—';

const ACTION_LABELS = {
  job_deleted: { label: 'Deleted', color: '#c62828' },
  job_restored: { label: 'Restored', color: '#1B5E20' },
  job_purged: { label: 'Purged', color: '#6a1a9a' },
};

export default function DataSafety() {
  const [tab, setTab] = useState('bin');
  const [bin, setBin] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    try {
      const [b, a] = await Promise.all([getRecycleBin().catch(() => ({ jobs: [] })), getAuditLog().catch(() => ({ entries: [] }))]);
      setBin(b.jobs || []);
      setAudit(a.entries || []);
    } finally { setLoading(false); }
  }

  async function handleRestore(job) {
    try { await restoreJob(job.id); toast.success(`Restored ${job.po_number || 'job'}`); load(); }
    catch (e) { toast.error('Restore failed: ' + (e?.response?.data?.error || '')); }
  }

  async function handlePurge(job) {
    if (!window.confirm(`PERMANENTLY delete ${job.po_number || 'this job'} (${job.customer_name || ''})?\n\nThis cannot be undone — it removes the record and its payments for good.`)) return;
    try { await purgeJob(job.id); toast.success('Permanently deleted'); load(); }
    catch (e) { toast.error('Purge failed: ' + (e?.response?.data?.error || '')); }
  }

  return (
    <>
      <div className="page-header">
        <h2>Data Safety</h2>
        <div style={{ fontSize: 13, color: '#888' }}>Recover deleted records and review activity</div>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          {[['bin', `🗑️ Recycle Bin${bin.length ? ` (${bin.length})` : ''}`], ['audit', '📜 Audit Log']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid ' + (tab === k ? GREEN : '#ccc'),
              background: tab === k ? GREEN : '#fff', color: tab === k ? '#fff' : '#555', fontWeight: 600, cursor: 'pointer',
            }}>{l}</button>
          ))}
        </div>

        {loading ? <div style={{ padding: 24, color: '#aaa' }}>Loading…</div> : tab === 'bin' ? (
          bin.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#888', background: '#fff', borderRadius: 10 }}>
              🎉 Recycle bin is empty — no deleted records.
            </div>
          ) : (
            <div className="card">
              <div style={{ padding: '12px 16px', background: '#fff8e1', fontSize: 13, color: '#8a6d00', borderRadius: '8px 8px 0 0' }}>
                Deleted records are kept here and stay out of all normal views. Restore brings them back; permanent delete is admin-only and cannot be undone.
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: GREEN, color: '#fff' }}>
                    {['PO #', 'Customer', 'Value', 'Deleted', 'By', 'Actions'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {bin.map((j, i) => (
                    <tr key={j.id} style={{ background: i % 2 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px 14px', fontWeight: 600 }}>{j.po_number || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>{j.customer_name || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>{money(j.change_order_amt || j.contract_amt)}</td>
                      <td style={{ padding: '10px 14px', fontSize: 12, color: '#777' }}>{when(j.deleted_at)}</td>
                      <td style={{ padding: '10px 14px', fontSize: 13, color: '#555' }}>{j.deleted_by || '—'}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => handleRestore(j)}>↩ Restore</button>
                          <button className="btn btn-ghost btn-sm" style={{ color: '#c62828' }} onClick={() => handlePurge(j)}>Delete forever</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          audit.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#888', background: '#fff', borderRadius: 10 }}>No activity recorded yet.</div>
          ) : (
            <div className="card">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: GREEN, color: '#fff' }}>
                    {['When', 'Action', 'Record', 'Who', 'Detail'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {audit.map((e, i) => {
                    const a = ACTION_LABELS[e.action] || { label: e.action, color: '#555' };
                    return (
                      <tr key={e.id || i} style={{ background: i % 2 ? '#fff' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px 14px', fontSize: 12, color: '#777' }}>{when(e.created_at)}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ background: a.color, color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>{a.label}</span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>{e.entity_label || (e.entity_id ? `#${e.entity_id}` : '—')}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>{e.actor || '—'}</td>
                        <td style={{ padding: '10px 14px', fontSize: 13, color: '#555' }}>{e.detail || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </>
  );
}
